import React, { useMemo, useState } from "react";
// Reuses the same modal shell + table styles as SessionHistoryModal
// (mac-modal*, session-table*) so this looks and scrolls exactly like
// it — including its mobile fix for the overlapping-column bug — instead
// of maintaining a second copy of that CSS here.
import "./InternetPlanCard.css";
import "./AuthLogsModal.css";

// Shows the RADIUS authentication log for a customer's connection —
// auth_logs.php looks the account's internet_username up and calls the
// broadband provider's /api/auth-logs with it, returning every
// Access-Accept / Access-Reject attempt the NAS has reported.
//
// Props:
//   open     — whether the modal is visible
//   onClose  — () => void
//   loading  — true while auth_logs.php is in flight
//   error    — error message string, or null
//   data     — the auth_logs.php success payload: { username, logs }
const AuthLogsModal = ({ open, onClose, loading, error, data }) => {
  // Accept/Reject filter — defaults to "all" since a rejected login is
  // usually exactly what someone opens this modal to find, but the
  // full history (including the accepts around it) is still useful
  // context, so nothing is hidden until they ask to narrow it down.
  const [filter, setFilter] = useState("all");

  const allLogs = Array.isArray(data?.logs) ? data.logs : [];

  const logs = useMemo(() => {
    if (filter === "all") return allLogs;
    return allLogs.filter((l) =>
      filter === "reject"
        ? /reject/i.test(l.reply || "")
        : /accept/i.test(l.reply || "")
    );
  }, [allLogs, filter]);

  const rejectCount = useMemo(
    () => allLogs.filter((l) => /reject/i.test(l.reply || "")).length,
    [allLogs]
  );

  if (!open) return null;

  const fmtDate = (value) => {
    if (!value) return "--";
    const d = new Date(value.replace(" ", "T"));
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="mac-modal-overlay">
      <div
        className="mac-modal"
        style={{ width: "95%", maxWidth: "min(1100px, 95vw)" }}
      >
        <div className="mac-header">
          <h2>Authentication Logs</h2>
          <button onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="auth-logs-modal-body">
          {loading && (
            <div className="auth-logs-modal-state">
              <span className="auth-logs-modal-spinner"></span>
              <p>Loading authentication logs…</p>
            </div>
          )}

          {!loading && error && (
            <div className="auth-logs-modal-state">
              <div className="auth-logs-modal-state-icon">⚠️</div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && allLogs.length === 0 && (
            <div className="auth-logs-modal-state">
              <div className="auth-logs-modal-state-icon">🔍</div>
              <p>No authentication attempts on file for this connection.</p>
            </div>
          )}

          {!loading && !error && allLogs.length > 0 && (
            <>
              <div className="auth-logs-modal-toolbar">
                <span className="auth-logs-modal-username">
                  {data?.username}
                </span>

                <div className="auth-logs-modal-filters">
                  <button
                    className={`auth-logs-filter-btn ${
                      filter === "all" ? "active" : ""
                    }`}
                    onClick={() => setFilter("all")}
                  >
                    All ({allLogs.length})
                  </button>
                  <button
                    className={`auth-logs-filter-btn reject ${
                      filter === "reject" ? "active" : ""
                    }`}
                    onClick={() => setFilter("reject")}
                  >
                    Rejected ({rejectCount})
                  </button>
                  <button
                    className={`auth-logs-filter-btn accept ${
                      filter === "accept" ? "active" : ""
                    }`}
                    onClick={() => setFilter("accept")}
                  >
                    Accepted ({allLogs.length - rejectCount})
                  </button>
                </div>
              </div>

              <div className="session-table-wrapper">
                <table className="session-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Result</th>
                      <th>Username Sent</th>
                      <th>MAC</th>
                      <th>NAS IP</th>
                      <th>NAS Port</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l, i) => {
                      const rejected = /reject/i.test(l.reply || "");
                      return (
                        <tr key={i}>
                          <td data-label="Time" className="auth-logs-nowrap">
                            {fmtDate(l.authdate)}
                          </td>
                          <td data-label="Result">
                            <span
                              className={`auth-logs-badge ${
                                rejected ? "reject" : "accept"
                              }`}
                            >
                              {l.reply || "--"}
                            </span>
                          </td>
                          <td data-label="Username Sent">{l.username_org || "--"}</td>
                          <td data-label="MAC" className="auth-logs-mono">
                            {l.mac || "--"}
                          </td>
                          <td data-label="NAS IP" className="auth-logs-mono">
                            {l.nasipaddress || "--"}
                          </td>
                          <td data-label="NAS Port" className="auth-logs-mono">
                            {l.nasportid || "--"}
                          </td>
                          <td data-label="Reason">{l.reason || "--"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthLogsModal;
